/**
 * The search bar's link previews, shared by the chip and the thread card.
 *
 * One entry per normalized address, module-level (no React): the chip asks
 * for a preview the moment a link is attached; when the shopper sends, the
 * chip goes away but the entry stays, so the card in the conversation shows
 * the same preview, including one that only arrives after the send.
 *
 *  - One request per address at a time (in-flight dedup).
 *  - Refcounted holds: removing the last chip of a link that is still
 *    loading aborts its request; a loaded preview is kept.
 *  - A 401/403 means no search proof yet AND a refused verdict, or a proof
 *    that expired: the entry is `forbidden` and is retried once the next
 *    search turn hands the bar a fresh pass (retryForbiddenLinkPreviews).
 *  - useSyncExternalStore-friendly: snapshots are stable objects until the
 *    entry changes.
 */

import { isLinkPreview, type LinkPreview } from 'shared/src/types/link-preview.type';

export type LinkPreviewStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'forbidden';

export interface LinkPreviewSnapshot {
  status: LinkPreviewStatus;
  preview: LinkPreview | null;
}

export interface FetchLinkPreviewOptions {
  /** Locale path, e.g. "en-CA". */
  locale: string;
  /** The page the chat is on, for the click log of the Amazon hop. */
  path?: string;
  headers?: Record<string, string>;
  /** Called with the response headers (to keep a search pass the route minted). */
  onResponseHeaders?: (headers: Headers) => void;
  fetchImpl?: typeof fetch;
}

interface Entry {
  snapshot: LinkPreviewSnapshot;
  holders: number;
  controller: AbortController | null;
  inflight: Promise<void> | null;
  listeners: Set<() => void>;
  lastRequest: Omit<FetchLinkPreviewOptions, 'headers' | 'fetchImpl'> | null;
}

const IDLE: LinkPreviewSnapshot = Object.freeze({ status: 'idle', preview: null }) as LinkPreviewSnapshot;
const entries = new Map<string, Entry>();

function entryFor(url: string): Entry {
  let entry = entries.get(url);
  if (!entry) {
    entry = { snapshot: IDLE, holders: 0, controller: null, inflight: null, listeners: new Set(), lastRequest: null };
    entries.set(url, entry);
  }
  return entry;
}

function set(url: string, snapshot: LinkPreviewSnapshot): void {
  const entry = entryFor(url);
  entry.snapshot = snapshot;
  for (const listener of [...entry.listeners]) listener();
}

export function getLinkPreviewSnapshot(url: string | null | undefined): LinkPreviewSnapshot {
  if (!url) return IDLE;
  return entries.get(url)?.snapshot ?? IDLE;
}

export function subscribeLinkPreview(url: string | null | undefined, listener: () => void): () => void {
  if (!url) return () => undefined;
  const entry = entryFor(url);
  entry.listeners.add(listener);
  return () => entry.listeners.delete(listener);
}

/** Seed a preview we already know (a resumed conversation, a fixture). */
export function primeLinkPreview(url: string, preview: LinkPreview): void {
  const current = entries.get(url)?.snapshot;
  if (current?.status === 'ready') return;
  set(url, { status: 'ready', preview });
}

/** Ask the server once for this address; resolves when the entry settles. */
export function fetchLinkPreview(url: string, opts: FetchLinkPreviewOptions): Promise<void> {
  const entry = entryFor(url);
  if (entry.inflight) return entry.inflight;
  if (entry.snapshot.status === 'ready') return Promise.resolve();
  entry.lastRequest = { locale: opts.locale, path: opts.path, onResponseHeaders: opts.onResponseHeaders };
  const controller = new AbortController();
  entry.controller = controller;
  set(url, { status: 'loading', preview: null });
  const fetchImpl = opts.fetchImpl ?? fetch;
  const run = (async () => {
    try {
      const response = await fetchImpl('/api/link-preview', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', ...(opts.headers ?? {}) },
        body: JSON.stringify({ url, locale: opts.locale, ...(opts.path ? { path: opts.path } : {}) }),
        signal: controller.signal,
      });
      opts.onResponseHeaders?.(response.headers);
      if (response.status === 401 || response.status === 403) {
        set(url, { status: 'forbidden', preview: null });
        return;
      }
      if (!response.ok) {
        set(url, { status: 'unavailable', preview: null });
        return;
      }
      const body = (await response.json()) as { preview?: unknown };
      if (isLinkPreview(body?.preview)) set(url, { status: 'ready', preview: body.preview });
      else set(url, { status: 'unavailable', preview: null });
    } catch {
      // An abort (the chip was removed) leaves the entry idle so a later
      // attach asks again; any other failure reads as unavailable.
      set(url, controller.signal.aborted ? IDLE : { status: 'unavailable', preview: null });
    } finally {
      if (entry.controller === controller) entry.controller = null;
      entry.inflight = null;
    }
  })();
  entry.inflight = run;
  return run;
}

/** A chip shows this link. */
export function holdLinkPreview(url: string): void {
  entryFor(url).holders += 1;
}

/**
 * A chip no longer shows this link. Removed by the shopper (`abort`): a
 * request nobody else holds is aborted. Sent (`abort: false`): the request
 * keeps going, the conversation's card is about to show its answer.
 */
export function releaseLinkPreview(url: string, { abort = true }: { abort?: boolean } = {}): void {
  const entry = entries.get(url);
  if (!entry) return;
  entry.holders = Math.max(0, entry.holders - 1);
  if (abort && entry.holders === 0 && entry.snapshot.status === 'loading') entry.controller?.abort();
}

/** Ask again for every preview the server refused for want of a search proof. */
export function retryForbiddenLinkPreviews(headers: Record<string, string>, fetchImpl?: typeof fetch): void {
  for (const [url, entry] of entries) {
    if (entry.snapshot.status !== 'forbidden' || !entry.lastRequest) continue;
    set(url, IDLE);
    void fetchLinkPreview(url, { ...entry.lastRequest, headers, fetchImpl });
  }
}

/** Test-only. */
export function _resetLinkPreviewCacheForTests(): void {
  for (const entry of entries.values()) entry.controller?.abort();
  entries.clear();
}
